import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { X } from "lucide-react";

import ApplicationOverlay from "../ApplicationOverlay";
import { cn } from "../../lib/utils";
import { Button } from "./button";

const sizeClasses = {
  confirmation: "flowpi-dialog--confirmation",
  form: "flowpi-dialog--form",
  workflow: "flowpi-dialog--workflow",
  inspection: "flowpi-dialog--inspection",
};

function useRestoreTriggerFocus(open) {
  const triggerRef = React.useRef(null);
  React.useLayoutEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    triggerRef.current = document.activeElement;
    return () => {
      const trigger = triggerRef.current;
      window.setTimeout(() => {
        if (trigger?.isConnected && typeof trigger.focus === "function") trigger.focus();
      }, 0);
    };
  }, [open]);
}

function closeButton(label, busy, Close, testId) {
  return (
    <Close asChild>
      <Button
        type="button"
        disabled={busy}
        aria-label={label}
        variant="ghost"
        size="icon"
        data-action-category="close"
        data-testid={testId}
      >
        <X className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
      </Button>
    </Close>
  );
}

function DialogFrame({
  eyebrow,
  title,
  description,
  closeLabel,
  closeTestId,
  busy,
  children,
  footer,
  HeaderTitle,
  HeaderDescription,
  Close,
  headerClassName,
  bodyClassName,
  footerClassName,
}) {
  return (
    <>
      <div className={cn("flowpi-dialog__header", headerClassName)} data-flowpi-dialog-header="">
        <div className="min-w-0">
          {eyebrow ? <div className="flowpi-dialog__eyebrow">{eyebrow}</div> : null}
          <HeaderTitle className="flowpi-dialog__title">{title}</HeaderTitle>
          <HeaderDescription className={description ? "flowpi-dialog__description" : "sr-only"}>
            {description || `${title} dialog.`}
          </HeaderDescription>
        </div>
        {closeButton(closeLabel || `Close ${title}`, busy, Close, closeTestId)}
      </div>
      <div className={cn("flowpi-dialog__body", bodyClassName)} data-flowpi-dialog-body="">
        {children}
      </div>
      {footer ? (
        <div className={cn("flowpi-dialog__footer", footerClassName)} data-flowpi-dialog-footer="">
          {footer}
        </div>
      ) : null}
    </>
  );
}

export function FlowpiDialog({
  open,
  onClose,
  busy = false,
  size = "form",
  eyebrow,
  title,
  description,
  closeLabel,
  closeTestId,
  children,
  footer,
  className,
  headerClassName,
  bodyClassName,
  footerClassName,
  testId,
}) {
  useRestoreTriggerFocus(open);
  const requestClose = React.useCallback(() => {
    if (!busy) onClose?.();
  }, [busy, onClose]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(nextOpen) => !nextOpen && requestClose()}>
      <DialogPrimitive.Portal>
        <ApplicationOverlay
          component={DialogPrimitive.Overlay}
          portal={false}
          data-flowpi-layer="modal-overlay"
        />
        <DialogPrimitive.Content
          aria-modal="true"
          className={cn("flowpi-dialog", sizeClasses[size] || sizeClasses.form, className)}
          data-flowpi-layer="modal"
          data-flowpi-dialog-size={size}
          data-testid={testId}
          onEscapeKeyDown={(event) => busy && event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <DialogFrame
            eyebrow={eyebrow}
            title={title}
            description={description}
            closeLabel={closeLabel}
            closeTestId={closeTestId}
            busy={busy}
            footer={footer}
            HeaderTitle={DialogPrimitive.Title}
            HeaderDescription={DialogPrimitive.Description}
            Close={DialogPrimitive.Close}
            headerClassName={headerClassName}
            bodyClassName={bodyClassName}
            footerClassName={footerClassName}
          >
            {children}
          </DialogFrame>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// Interaction/sizing adapter for established complex workflows whose domain
// composition already owns its header, body, and footer markup.
export function FlowpiDialogSurface({
  open,
  onClose,
  busy = false,
  size = "workflow",
  accessibleTitle,
  accessibleDescription,
  children,
  className,
  testId,
}) {
  useRestoreTriggerFocus(open);
  const requestClose = React.useCallback(() => {
    if (!busy) onClose?.();
  }, [busy, onClose]);

  const [surface, ...nestedLayers] = React.Children.toArray(children);
  const accessibleSurface = React.isValidElement(surface)
    ? React.cloneElement(
        surface,
        {},
        <DialogPrimitive.Title className="sr-only">{accessibleTitle}</DialogPrimitive.Title>,
        <DialogPrimitive.Description className="sr-only">{accessibleDescription || `${accessibleTitle} dialog.`}</DialogPrimitive.Description>,
        surface.props.children
      )
    : surface;
  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={(nextOpen) => !nextOpen && requestClose()}>
        <DialogPrimitive.Portal>
          <ApplicationOverlay component={DialogPrimitive.Overlay} portal={false} data-flowpi-layer="modal-overlay" />
          <DialogPrimitive.Content
            asChild
            aria-modal="true"
            className={cn("flowpi-dialog", sizeClasses[size] || sizeClasses.workflow, className)}
            data-flowpi-layer="modal"
            data-flowpi-dialog-size={size}
            data-testid={testId}
            onEscapeKeyDown={(event) => busy && event.preventDefault()}
            onInteractOutside={(event) => event.preventDefault()}
            onPointerDownOutside={(event) => event.preventDefault()}
          >
            {accessibleSurface}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      {nestedLayers}
    </>
  );
}

export function FlowpiDrawerSurface({
  open,
  onClose,
  busy = false,
  accessibleTitle,
  children,
  className,
  testId,
}) {
  useRestoreTriggerFocus(open);
  const requestClose = React.useCallback(() => {
    if (!busy) onClose?.();
  }, [busy, onClose]);
  const accessibleSurface = React.isValidElement(children)
    ? React.cloneElement(
        children,
        {},
        <DialogPrimitive.Title className="sr-only">{accessibleTitle}</DialogPrimitive.Title>,
        <DialogPrimitive.Description className="sr-only">{accessibleTitle} drawer.</DialogPrimitive.Description>,
        children.props.children
      )
    : children;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(nextOpen) => !nextOpen && requestClose()}>
      <DialogPrimitive.Portal>
        <ApplicationOverlay component={DialogPrimitive.Overlay} portal={false} data-flowpi-layer="modal-overlay" />
        <DialogPrimitive.Content
          asChild
          aria-modal="true"
          className={cn("flowpi-drawer", className)}
          data-flowpi-layer="modal"
          data-testid={testId}
          onEscapeKeyDown={(event) => busy && event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          {accessibleSurface}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function FlowpiAlertDialog({
  open,
  onClose,
  busy = false,
  size = "confirmation",
  eyebrow,
  title,
  description,
  closeLabel,
  closeTestId,
  children,
  footer,
  className,
  bodyClassName,
  testId,
}) {
  useRestoreTriggerFocus(open);
  const requestClose = React.useCallback(() => {
    if (!busy) onClose?.();
  }, [busy, onClose]);

  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={(nextOpen) => !nextOpen && requestClose()}>
      <AlertDialogPrimitive.Portal>
        <ApplicationOverlay
          component={AlertDialogPrimitive.Overlay}
          portal={false}
          className="flowpi-dialog-overlay--critical"
          data-flowpi-layer="critical-confirmation-overlay"
        />
        <AlertDialogPrimitive.Content
          aria-modal="true"
          className={cn("flowpi-dialog flowpi-dialog--critical", sizeClasses[size] || sizeClasses.confirmation, className)}
          data-flowpi-layer="critical-confirmation"
          data-flowpi-dialog-size={size}
          data-testid={testId}
          onEscapeKeyDown={(event) => busy && event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <DialogFrame
            eyebrow={eyebrow}
            title={title}
            description={description}
            closeLabel={closeLabel}
            closeTestId={closeTestId}
            busy={busy}
            footer={footer}
            HeaderTitle={AlertDialogPrimitive.Title}
            HeaderDescription={AlertDialogPrimitive.Description}
            Close={AlertDialogPrimitive.Cancel}
            bodyClassName={bodyClassName}
          >
            {children}
          </DialogFrame>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
